import { action, makeObservable, observable, reaction } from 'mobx';
import {
  DEFAULT_GAME_RESULT_VALUE,
  DEFAULT_MULTIPLIER_VALUE,
  DEFAULT_WIN_CHANCE_VALUE,
  Limbo10SoundsName,
  LIMBO_10_BET_ITEMS,
  LIMBO_10_DEFAULT_MULTIPLIERS,
  MULTIPLIER_LIMIT,
  MULTIPLIER_PRECISION,
  MULTIPLIER_STEP_VALUE,
  RTP,
  SINGLE_LIMBO_MODEL,
  WIN_CHANCE_LIMIT,
  WIN_CHANCE_PRECISION,
  WIN_CHANCE_STEP_VALUE,
} from '../limbo10Core/constants';
import { parseNumberPrecisionByDoteCount } from '../../../core/helpers/parseNumberByPrecision';
import { soundService } from '../../../services/baseSoundService/soundService';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { checkHashStore } from '../../../store/checkHashStore';
import { betHistoryStore } from '../../../store/betHistoryStore';
import {
  countOfIncrease,
  increaseIntervalHelper,
  resultSoundTimeOutHelper,
} from '../limbo10Core/helpers';
import { userInfoStore } from '../../../store/userInfoStore';
import { mathConfig, mathJS } from '../../../core/helpers/mathFormat';

class Limbo10BetStore {
  multiplier = DEFAULT_MULTIPLIER_VALUE;

  winChance = DEFAULT_WIN_CHANCE_VALUE;

  gameResult = DEFAULT_GAME_RESULT_VALUE;

  isBetsClosed = false;

  lastResults = [];

  roundIsWin = null;

  isShowRoundWinLoseState = false;

  winAmount = null;

  winOdd = null;

  maxWinLimit = null;

  allLimboBetFields = [];

  constructor() {
    makeObservable(this, {
      winOdd: observable,
      winAmount: observable,
      allLimboBetFields: observable.deep,
      winChance: observable,
      multiplier: observable,
      isShowRoundWinLoseState: observable,
      maxWinLimit: observable,
      gameResult: observable,
      roundIsWin: observable,
      isBetsClosed: observable,
      setRoundIsWin: observable,
      lastResults: observable.deep,
      setWinOdd: action.bound,
      setWinAmount: action.bound,
      handleUpdateAllLimboBetFields: action.bound,
      handleCurrentLimboBetField: action.bound,
      setIsShowRoundWinLoseState: action.bound,
      setWinChance: action.bound,
      setMultiplier: action.bound,
      setMaxWinLimit: action.bound,
      setGameResult: action.bound,
      updatePayout: action.bound,
      setLastResults: action.bound,
      setIsBetsClosed: action.bound,
      updateResultOdd: action.bound,
      updateLastResults: action.bound,
      handleIncreaseWinChance: action.bound,
      handleIncreaseMultiplier: action.bound,
      updateResultSaltAndKey: action.bound,
      handleInitStateBetFields: action.bound,
    });

    reaction(
      () => this.isBetsClosed,
      (value) => {
        userInfoStore.setIsAllowedChangeCurrency(!value);
      }
    );

    reaction(
      () => userInfoStore.betAmount,
      (value) => {
        this.allLimboBetFields.forEach((singleLimbo) => {
          singleLimbo.payout = parseNumberPrecisionByDoteCount(
            value,
            +singleLimbo.multiplier,
            userInfoStore.precision
          );
        });
      }
    );
  }

  setMaxWinLimit(limit) {
    this.maxWinLimit = limit;
  }

  getCurrentLimbo(index) {
    return this.allLimboBetFields[index];
  }

  setWinOdd(odd) {
    this.winOdd = odd;
    clearTimeout(this.timeOutOdd);
    this.timeOutOdd = setTimeout(() => {
      this.winOdd = null;
    }, 1500);
  }

  setWinAmount(amount) {
    this.winAmount = amount;
    clearTimeout(this.timeOutWinAmount);
    this.timeOutWinAmount = setTimeout(() => {
      this.winAmount = null;
    }, 2000);
  }

  handleInitStateBetFields() {
    Object.values(LIMBO_10_BET_ITEMS).forEach((value, index) => {
      const multiplier = LIMBO_10_DEFAULT_MULTIPLIERS[index];
      const winChance = parseNumberPrecisionByDoteCount(
        100,
        +mathJS.format(RTP / +multiplier, mathConfig),
        WIN_CHANCE_PRECISION
      );
      this.allLimboBetFields[value] = {
        marketType: value,
        ...SINGLE_LIMBO_MODEL({ betAmount: userInfoStore.betAmount, multiplier, winChance }),
      };
    });
  }

  updateResultSaltAndKey({ subSpins, salt, key, hash, newHash, odd, id }) {
    checkHashStore.setLastRoundInfo({ salt, key, hash });
    checkHashStore.setNextRoundHash(newHash);
    checkHashStore.setLastRoundResult({ subSpins, odd, id });
  }

  setRoundIsWin(isWin) {
    this.roundIsWin = isWin;
  }

  setIsShowRoundWinLoseState(bool) {
    this.isShowRoundWinLoseState = bool;
  }

  setLastResults(lastResults) {
    this.lastResults = lastResults.map((item, index) => ({ ...item, id: index }));
  }

  updateLastResults(lastResult) {
    this.lastResults = [...this.lastResults, lastResult];
    setTimeout(() => {
      this.lastResults = [this.lastResults[this.lastResults.length - 1]];
    }, 1000);
    // if (updatedLastResult.length > (isMobile() ? 3 : 5)) {
    //   updatedLastResult.splice(0, 1);
    // }
    // const lastEl = updatedLastResult.pop();
    // this.setLastResults(updatedLastResult);
    // setTimeout(() => {
    //   this.setLastResults([...updatedLastResult, lastEl]);
    // }, 0);
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  updatePayout(index) {
    const { multiplier } = this.getCurrentLimbo(index);
    this.getCurrentLimbo(index).payout = parseNumberPrecisionByDoteCount(
      userInfoStore.betAmount,
      multiplier,
      userInfoStore.precision
    );
  }

  setGameResult(odd, isFinished) {
    this.gameResult = odd && Number(odd);
    if (isFinished) {
      this.gameResult = odd && Number(odd).toFixed(2);
    }
  }

  handleCurrentLimboBetField(marketType, item) {
    const currentFieldIndex = this.allLimboBetFields.findIndex(
      (field) => field.marketType === marketType
    );

    if (currentFieldIndex >= 0) {
      const newFields = [...this.allLimboBetFields];
      newFields[currentFieldIndex] = { ...newFields[currentFieldIndex], ...item };
      this.allLimboBetFields = newFields;
    }
  }

  handleRoundEnd({ id, key, hash, salt, winOdd, newHash, subSpins, callBack, allWinAmount }) {
    setTimeout(() => {
      if (userSettingsStore.isSoundOn) {
        soundService.play(Limbo10SoundsName.countResult);
      }
      this.updateResultSaltAndKey({ subSpins, salt, key, hash, newHash, id });
      this.handleRoundFinished({
        resultModel: subSpins,
        winAmount: allWinAmount,
        winOdd,
        callBack,
      });
    }, 500);
  }

  handleUpdateAllLimboBetFields({
    id,
    key,
    salt,
    hash,
    winOdd,
    newHash,
    subSpins,
    callBack,
    allWinAmount,
  }) {
    const bigSubSpinMultiplier = subSpins.reduce((acc, cur) => {
      if (cur.resultMultiplier > acc) {
        acc = cur.resultMultiplier;
      }
      return acc;
    }, 0);

    if (!userSettingsStore.isTurboOn && userSettingsStore.isSoundOn) {
      soundService.play(Limbo10SoundsName.counting);
    }
    for (let i = 0; i < subSpins.length; i += 1) {
      const { winAmount, marketType, resultMultiplier } = subSpins[i];
      if (userSettingsStore.isTurboOn) {
        this.handleCurrentLimboBetField(marketType, subSpins[i]);
        if (bigSubSpinMultiplier === resultMultiplier) {
          this.handleRoundEnd({
            id,
            key,
            hash,
            salt,
            winOdd,
            newHash,
            subSpins,
            callBack,
            allWinAmount,
          });
        }
      } else {
        const increaseCount = countOfIncrease(resultMultiplier);
        let step = 0;
        let newOdd = 0;
        let resultOdd = DEFAULT_GAME_RESULT_VALUE;
        const percent = 0.01;

        const recursiveIncrease = () => {
          setTimeout(() => {
            if (+newOdd < resultMultiplier) {
              step = (resultMultiplier * percent).toFixed(3);
              newOdd = (+resultOdd || 1) + +step;
              if (newOdd >= resultMultiplier) {
                this.handleCurrentLimboBetField(marketType, subSpins[i]);
                if (newOdd >= bigSubSpinMultiplier) {
                  if (userSettingsStore.isSoundOn) {
                    soundService.stop(Limbo10SoundsName.counting);
                  }
                  this.handleRoundEnd({
                    id,
                    key,
                    hash,
                    salt,
                    winOdd,
                    newHash,
                    subSpins,
                    callBack,
                    allWinAmount,
                  });
                }
                return null;
              }
              resultOdd = +newOdd.toFixed(2);
              this.handleCurrentLimboBetField(marketType, { resultMultiplier: resultOdd });
              recursiveIncrease();
            }
            return null;
          }, increaseIntervalHelper(resultMultiplier, Boolean(winAmount)) / increaseCount);
        };
        recursiveIncrease();
      }
    }
    //
    // subSpins.forEach((item) => {
    //   const { winAmount, marketType, resultMultiplier } = item;
    //   if (userSettingsStore.isTurboOn) {
    //     this.handleCurrentLimboBetField(marketType, item);
    //     if (bigSubSpinMultiplier === resultMultiplier) {
    //       this.handleRoundEnd({
    //         id,
    //         key,
    //         hash,
    //         salt,
    //         winOdd,
    //         newHash,
    //         subSpins,
    //         callBack,
    //         allWinAmount,
    //       });
    //     }
    //   } else {
    //     const increaseCount = countOfIncrease(resultMultiplier);
    //     let step = 0;
    //     let newOdd = 0;
    //     let resultOdd = DEFAULT_GAME_RESULT_VALUE;
    //     const percent = 0.01;
    //
    //     const recursiveIncrease = () => {
    //       setTimeout(() => {
    //         if (+newOdd < resultMultiplier) {
    //           step = (resultMultiplier * percent).toFixed(3);
    //           newOdd = (+resultOdd || 1) + +step;
    //           if (newOdd >= resultMultiplier) {
    //             this.handleCurrentLimboBetField(marketType, item);
    //             if (newOdd >= bigSubSpinMultiplier) {
    //               if (userSettingsStore.isSoundOn) {
    //                 soundService.stop(Limbo10SoundsName.counting);
    //               }
    //               this.handleRoundEnd({
    //                 id,
    //                 key,
    //                 hash,
    //                 salt,
    //                 winOdd,
    //                 newHash,
    //                 subSpins,
    //                 callBack,
    //                 allWinAmount,
    //               });
    //             }
    //             return null;
    //           }
    //           resultOdd = +newOdd.toFixed(2);
    //           this.handleCurrentLimboBetField(marketType, { resultMultiplier: resultOdd });
    //           recursiveIncrease();
    //         }
    //         return null;
    //       }, increaseIntervalHelper(resultMultiplier, Boolean(winAmount)) / increaseCount);
    //     };
    //     recursiveIncrease();
    //   }
    // });
  }

  handleRoundFinished({ resultModel, winAmount, winOdd, callBack }) {
    this.updateLastResults(resultModel);
    betHistoryStore.getLastBet();
    userInfoStore.getBalance();
    setTimeout(() => {
      this.setWinOdd(winOdd);
      this.setWinAmount(winAmount);
    }, 1000);
    this.setIsShowRoundWinLoseState(true);
    setTimeout(() => {
      this.setIsBetsClosed(false);
      this.setIsShowRoundWinLoseState(false);
      callBack();
    }, 2000);
  }

  updateResultOdd({ odd, resultModel, key, salt, hash, newHash, id, winAmount, winOdd }) {
    const increaseCount = countOfIncrease(odd);
    let step;
    let newOdd;
    const percent = 0.01;
    if (userSettingsStore.isSoundOn) {
      if (userSettingsStore.isTurboOn) {
        soundService.play(
          resultModel.isWin ? Limbo10SoundsName.winTurbo : Limbo10SoundsName.loseTurbo
        );
      } else {
        setTimeout(() => {
          if (resultModel.isWin) {
            soundService
              .play(odd <= 1.5 ? Limbo10SoundsName.shortWin : Limbo10SoundsName.longWin)
              .then(() => {});
          } else {
            soundService
              .play(odd <= 1.5 ? Limbo10SoundsName.shortLose : Limbo10SoundsName.longLose)
              .then(() => {});
          }
        }, resultSoundTimeOutHelper(odd) / increaseCount);
      }
    }
    if (!userSettingsStore.isTurboOn) {
      // const time = performance.now()
      const recursiveIncrease = () => {
        setTimeout(() => {
          if (+this.gameResult < odd) {
            step = (odd * percent).toFixed(3);
            if (+this.gameResult + +step >= odd) {
              this.updateRoundState(odd, resultModel, winAmount, winOdd);
              this.updateResultSaltAndKey({ salt, key, hash, newHash, odd, id });
              return null;
            }
            newOdd = (+this.gameResult || 1) + +step;
          } else {
            step = (this.gameResult * percent).toFixed(3);
            if (+this.gameResult - +step <= +odd) {
              this.updateRoundState(odd, resultModel, winAmount, winOdd);
              this.updateResultSaltAndKey({ salt, key, hash, newHash, odd, id });
              this.setIsBetsClosed(false);
              return null;
            }
            newOdd = (this.gameResult || 1) - +step;
          }
          if (+this.gameResult === null) {
            this.setGameResult(DEFAULT_GAME_RESULT_VALUE);
          }
          this.setGameResult(+newOdd.toFixed(2));
          recursiveIncrease();
          return null;
        }, increaseIntervalHelper(odd, resultModel.isWin) / increaseCount);
      };
      recursiveIncrease();
    } else {
      this.updateRoundState(odd, resultModel, winAmount, winOdd);
      this.updateResultSaltAndKey({ salt, key, hash, newHash, odd, id });
    }
  }

  updateRoundState(odd, resultModel, winAmount, winOdd) {
    this.setGameResult(odd, true);
    this.updateLastResults(resultModel);
    this.setIsBetsClosed(false);
    betHistoryStore.getLastBet();
    userInfoStore.getBalance();
    setTimeout(() => {
      this.setWinAmount(winAmount);
      this.setWinOdd(winOdd);
    }, 1000);
  }

  setWinChance = (winChance, index) => {
    if (index >= 0) {
      this.getCurrentLimbo(index).winChance = winChance;
      let newMultiplier = parseNumberPrecisionByDoteCount(
        100,
        +mathJS.format(RTP / +winChance, mathConfig),
        MULTIPLIER_PRECISION
      );
      if (newMultiplier < MULTIPLIER_LIMIT.minBet) {
        newMultiplier = MULTIPLIER_LIMIT.minBet;
      }
      if (newMultiplier > MULTIPLIER_LIMIT.maxBet) {
        newMultiplier = MULTIPLIER_LIMIT.maxBet;
      }
      this.getCurrentLimbo(index).multiplier = Number.isFinite(newMultiplier) ? +newMultiplier : 0;
      this.updatePayout(index);
    }
  };

  setMultiplier(multiplier, index) {
    this.getCurrentLimbo(index).multiplier = multiplier;
    let newWinChance = parseNumberPrecisionByDoteCount(
      100,
      +mathJS.format(RTP / +multiplier, mathConfig),
      WIN_CHANCE_PRECISION
    );
    if (newWinChance < WIN_CHANCE_LIMIT.minBet) {
      newWinChance = WIN_CHANCE_LIMIT.minBet;
    }
    if (newWinChance > WIN_CHANCE_LIMIT.maxBet) {
      newWinChance = WIN_CHANCE_LIMIT.maxBet;
    }
    this.getCurrentLimbo(index).winChance = Number.isFinite(newWinChance) ? +newWinChance : 0;
    this.updatePayout(index);
  }

  handleIncreaseWinChance(isIncrease, index) {
    const nextWinChanceValue =
      +this.getCurrentLimbo(index).winChance +
      +mathJS.format((isIncrease ? 1 : -1) * WIN_CHANCE_STEP_VALUE, mathConfig);
    if (nextWinChanceValue > WIN_CHANCE_LIMIT.maxBet) {
      this.setWinChance(WIN_CHANCE_LIMIT.maxBet, index);
    } else if (nextWinChanceValue < WIN_CHANCE_LIMIT.minBet) {
      this.setWinChance(WIN_CHANCE_LIMIT.minBet, index);
    } else {
      const newWinChance = parseNumberPrecisionByDoteCount(
        1,
        nextWinChanceValue,
        WIN_CHANCE_PRECISION
      );
      this.setWinChance(Number.isFinite(newWinChance) ? +newWinChance : 0, index);
    }
  }

  handleIncreaseMultiplier(isIncrease, index) {
    const nextMultiplierValue =
      +this.getCurrentLimbo(index).multiplier +
      +mathJS.format((isIncrease ? 1 : -1) * MULTIPLIER_STEP_VALUE, mathConfig);
    if (nextMultiplierValue > MULTIPLIER_LIMIT.maxBet) {
      this.setMultiplier(MULTIPLIER_LIMIT.maxBet, index);
    } else if (nextMultiplierValue < MULTIPLIER_LIMIT.minBet) {
      this.setMultiplier(MULTIPLIER_LIMIT.minBet, index);
    } else {
      const newMultiplier = parseNumberPrecisionByDoteCount(
        1,
        nextMultiplierValue,
        MULTIPLIER_PRECISION
      );
      this.setMultiplier(Number.isFinite(newMultiplier) ? +newMultiplier : 0, index);
    }
  }
}

export const limbo10BetStore = new Limbo10BetStore();
